import { useEffect, useState } from "react";
import { sanitizeHtml } from "../lib/sanitize";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Radar as RadarIcon, Link2, MapPin, Loader2, Search, SearchX, RotateCw, Trash2, Sparkles, Play, BellRing, Mail, Clock, X, ChevronDown, Check, Gavel, ExternalLink, Lock, CheckSquare } from "lucide-react";

const CCY_SYMBOLS = { EUR: "€", USD: "$", CAD: "CA$", AUD: "A$", AED: "AED", TRY: "₺", GBP: "£", CHF: "CHF" };
const CCY_PREFIX = { USD: true, CAD: true, AUD: true, GBP: true };
const SQFT_PER_M2 = 10.7639;

// Formate un montant stocké en EUR vers la devise d'affichage choisie (conversion FX live).
function formatMoney(eur, ccy, rates, suffix = "") {
  if (eur === null || eur === undefined || eur === "") return "n.c.";
  const rate = ccy === "EUR" ? 1 : (rates?.[ccy] || null);
  if (!rate) return "n.c.";
  const v = Math.round(Number(eur) * rate);
  const sym = CCY_SYMBOLS[ccy] || ccy;
  const num = v.toLocaleString("fr-FR");
  const base = CCY_PREFIX[ccy] ? `${sym}${num}` : `${num} ${sym}`;
  return suffix ? `${base}${suffix}` : base;
}

// Surface : pieds carrés pour les biens US (stockés en m²), sinon m².
function formatSurface(l) {
  if (!l?.surface) return null;
  const isUS = l.country === "US" || l.unit === "sqft";
  if (isUS) {
    const sqft = Math.round(Number(l.surface) * SQFT_PER_M2);
    return `${sqft.toLocaleString("en-US")} sq ft (${Math.round(l.surface)} m²)`;
  }
  return `${Math.round(l.surface)} m²`;
}
import RadarDetail from "@/components/RadarDetail";
import RadarMap from "@/components/RadarMap";
import RadarTable from "@/components/RadarTable";
import GeoSelector from "@/components/GeoSelector";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

export const RADAR_TYPES = [
  "Maison", "Villa", "Appartement", "Immeuble", "Terrain", "Entrepôt",
  "Local commercial", "Bureaux", "Hôtel", "Domaine", "Château", "Autre",
];
const PERIODS = [
  { label: "24 h", days: 1 }, { label: "3 j", days: 3 }, { label: "7 j", days: 7 },
  { label: "14 j", days: 14 }, { label: "30 j", days: 30 }, { label: "Tout", days: 0 },
];
const FREQUENCIES = [
  { v: 60, label: "Toutes les heures" }, { v: 180, label: "Toutes les 3 h" },
  { v: 360, label: "Toutes les 6 h" }, { v: 720, label: "2 fois par jour" },
  { v: 1440, label: "1 fois par jour" },
];
const PROVIDER_LABELS = {
  moteurimmo: "MoteurImmo (56+ portails)",
  realtyapi: "RealtyAPI (SeLoger)",
  cherchertrouver: "ChercherTrouver",
};

const scoreColor = (s) =>
  s >= 70 ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : s >= 40 ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-slate-100 text-slate-600 border-slate-200";

const EMPTY = { url: "", address: "", title: "", city: "", arrondissement: "", quartier: "",
  property_type: "", price: "", surface: "", land_surface: "", description: "" };

export default function Radar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("auto");
  const [hasSearched, setHasSearched] = useState(false); // affiche les résultats sous les filtres après recherche
  const [searchResults, setSearchResults] = useState(null); // résultats de LA dernière recherche (isolés du global)
  const [searchMeta, setSearchMeta] = useState(null); // { count, ranAt, criteriaLabel, status, budgetBlocked, source, analyzed, new }
  const [watchBannerOff, setWatchBannerOff] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [analyzing, setAnalyzing] = useState(false);
  const [listings, setListings] = useState([]);
  const [searches, setSearches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [period, setPeriod] = useState(0);
  const [mapFilter, setMapFilter] = useState("all");
  const [filters, setFilters] = useState({ q: "", type: "all", maxPrice: "", maxPm2: "", minSurface: "" });

  // Recherche automatique (source connectée)
  const [source, setSource] = useState({ provider: "apify", configured: false, providers: {}, meta: {} });
  const [apiKey, setApiKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [searching, setSearching] = useState(false);
  const EMPTY_CRIT = { country: "FR", state: "", county: "", villes: "", cps: "", property_types: [], prix_min: "", prix_max: "", prix_m2_max: "", surface_min: "", surface_max: "", days: "0", exclude: "", radius_km: "0" };
  const [crit, setCrit] = useState(EMPTY_CRIT);
  const setC = (k, v) => setCrit((c) => ({ ...c, [k]: v }));
  // Sélections géographiques structurées (villes / CP / départements / régions).
  const [geoChips, setGeoChips] = useState([]);
  const chipsFromCrit = (c) => {
    const out = [];
    (c.villes ? String(c.villes).split(",").map((s) => s.trim()).filter(Boolean) : [])
      .forEach((v) => out.push({ type: "city", id: "city-" + v, label: v, city: v }));
    (c.cps ? String(c.cps).split(",").map((s) => s.trim()).filter(Boolean) : [])
      .forEach((v) => out.push({ type: "postal", id: "cp-" + v, label: v, postal: v }));
    (c.county ? String(c.county).split(",").map((s) => s.trim()).filter(Boolean) : [])
      .forEach((v) => out.push({ type: "department", id: "dep-" + v, label: v, department: v }));
    (c.state ? String(c.state).split(",").map((s) => s.trim()).filter(Boolean) : [])
      .forEach((v) => out.push({ type: "region", id: "reg-" + v, label: v, region: v }));
    return out;
  };
  // Répercute les chips sélectionnés dans les critères (villes/cps/county/state).
  useEffect(() => {
    const pick = (ty, f) => geoChips.filter((c) => c.type === ty).map((c) => c[f] || c.label);
    setCrit((c) => ({
      ...c,
      villes: pick("city", "city").join(", "),
      cps: pick("postal", "postal").join(", "),
      county: pick("department", "department").join(", "),
      state: pick("region", "region").join(", "),
    }));
  }, [geoChips]);
  const resetCriteria = () => { setCrit(EMPTY_CRIT); setGeoChips([]); toast.success(t("radar.criteria.resetToast")); };
  const [countries, setCountries] = useState([]);
  const toggleType = (t) => setCrit((c) => ({ ...c, property_types: c.property_types.includes(t) ? c.property_types.filter((x) => x !== t) : [...c.property_types, t] }));
  const [prefs, setPrefs] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [auctProviders, setAuctProviders] = useState({});
  const [auctSelected, setAuctSelected] = useState([]);
  const [auctKeys, setAuctKeys] = useState({});
  const [auctConfigured, setAuctConfigured] = useState({});
  const [auctMeta, setAuctMeta] = useState({});
  const [auctionScanning, setAuctionScanning] = useState(false);
  const [showAuctionsOnly, setShowAuctionsOnly] = useState(false);
  const [ent, setEnt] = useState(null);
  const [displayCcy, setDisplayCcy] = useState(localStorage.getItem("radar_ccy") || "EUR");
  const [fxRates, setFxRates] = useState({});
  const [mapFocus, setMapFocus] = useState(null); // secteur recherché : {lat, lon, radius_m, label}
  const [hoveredId, setHoveredId] = useState(null); // liaison tableau ↔ carte
  const [selectedResults, setSelectedResults] = useState(new Set()); // sélection multiple (onglet Résultats)
  const [confirmDelete, setConfirmDelete] = useState(null); // { mode: 'selection'|'all', count }
  const [deletingResults, setDeletingResults] = useState(false);
  const [confirmDeleteSearch, setConfirmDeleteSearch] = useState(null); // recherche auto à supprimer (confirmation)
  const [deletingSearch, setDeletingSearch] = useState(false);
  // Sources premium cochables (Green-Acres, Ventes notariales, Enchères)
  const [extraSources, setExtraSources] = useState([]);
  const [extraSelected, setExtraSelected] = useState([]);
  const [extraRunning, setExtraRunning] = useState(false);
  const loadExtraSources = () => api.get("/radar/extra-sources").then((r) => setExtraSources(r.data.sources || [])).catch(() => {});
  const toggleExtra = (sid, locked) => { if (locked) return; setExtraSelected((s) => s.includes(sid) ? s.filter((x) => x !== sid) : [...s, sid]); };
  const runExtraSources = async () => {
    const chosen = extraSelected.filter((sid) => { const s = extraSources.find((x) => x.id === sid); return s && !s.locked; });
    if (!chosen.length) { toast.error(t("radar.premium.needSource")); return; }
    setExtraRunning(true);
    let totalNew = 0;
    try {
      for (const sid of chosen) {
        try {
          const r = await api.post("/radar/extra-search", { ...buildCriteria(), source_id: sid });
          totalNew += r.data.new || 0;
          if (r.data.center) setMapFocus(r.data.center);
          const badge = r.data.cached ? " (cache 24 h — 0 appel Apify)" : "";
          toast.success(`${r.data.source} : ${r.data.analyzed} analysée(s), ${r.data.new} nouvelle(s)${badge}`);
        } catch (e) {
          toast.error(`${sid} : ${e.response?.data?.detail || "échec"}`);
        }
      }
      loadListings();
      if (totalNew >= 0) setTab("results");
    } finally { setExtraRunning(false); }
  };

  useEffect(() => {
    api.get("/radar/fx?base=EUR").then((r) => setFxRates(r.data?.rates || {})).catch(() => {});
  }, []);
  const changeCcy = (v) => { setDisplayCcy(v); localStorage.setItem("radar_ccy", v); };
  const loadEnt = () => api.get("/radar/entitlements").then((r) => setEnt(r.data)).catch(() => {});
  const quotaWatches = ent?.quota_watches ?? null; // null = illimité
  const activeWatches = ent?.active_watches ?? 0;
  const quotaReached = quotaWatches !== null && activeWatches >= quotaWatches;
  const canAuctions = ent ? !!ent.entitlements?.auctions : true;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [appliedZone, setAppliedZone] = useState(null); // { villes:[], cps:[], radius_km, center } de la dernière recherche
  const loadListings = (zone = appliedZone) => {
    const p = new URLSearchParams();
    if (zone?.radius_km && zone?.center?.lat != null) {
      // Mode rayon : filtre par distance au centre du secteur recherché.
      p.set("lat", zone.center.lat); p.set("lon", zone.center.lon); p.set("radius_km", zone.radius_km);
    } else {
      if (zone?.villes?.length) p.set("villes", zone.villes.join(","));
      if (zone?.cps?.length) p.set("cps", zone.cps.join(","));
    }
    // Pays STRICT : ne renvoie que les annonces du pays recherché (isole les autres pays / anciennes recherches).
    if (zone?.country) p.set("country", zone.country);
    const qs = p.toString();
    return api.get(`/radar/listings${qs ? `?${qs}` : ""}`).then((r) => { setListings(r.data); return r.data; }).catch(() => []);
  };
  const loadSearches = () => api.get("/radar/searches").then((r) => setSearches(r.data)).catch(() => {});
  const loadPrefs = () => api.get("/radar/notif-prefs").then((r) => setPrefs(r.data)).catch(() => {});
  useEffect(() => { loadListings(); loadSearches(); loadPrefs(); loadEnt(); loadExtraSources(); api.get("/radar/source").then((r) => setSource(r.data)).catch(() => {}); api.get("/radar/countries").then((r) => setCountries((r.data.countries || []).filter((c) => c.enabled))).catch(() => {}); api.get("/radar/auction-config").then((r) => { setAuctProviders(r.data.providers); setAuctSelected(r.data.selected); setAuctConfigured(r.data.configured || {}); setAuctMeta(r.data.meta || {}); }).catch(() => {}); }, []);

  const auctProviderKeys = Object.keys(auctProviders);
  const isSelectable = (p) => (auctMeta[p]?.selectable ?? true);
  const selectableKeys = auctProviderKeys.filter(isSelectable);
  const toggleAuctProv = (p) => { if (!isSelectable(p)) return; setAuctSelected((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]); };
  const selectAllAuctProv = () => setAuctSelected((s) => s.length === selectableKeys.length ? [] : [...selectableKeys]);
  const saveAuctionConfig = async () => {
    try {
      const r = await api.put("/radar/auction-config", { selected: auctSelected, keys: auctKeys });
      setAuctProviders(r.data.providers); setAuctSelected(r.data.selected); setAuctConfigured(r.data.configured || {}); setAuctMeta(r.data.meta || {});
      toast.success(t("radar.auctions.savedSources"));
    } catch { toast.error(t("radar.auctions.saveFail")); }
  };
  const openSourceSite = (p) => {
    const m = auctMeta[p] || {};
    const url = m.url;
    if (!url) return;
    const parts = [];
    if (crit.villes) parts.push(crit.villes);
    if (crit.cps) parts.push(crit.cps);
    if (crit.property_types?.length) parts.push(crit.property_types.join(", "));
    const q = parts.join(" · ");
    if (q && navigator.clipboard) { navigator.clipboard.writeText(q).catch(() => {}); }
    window.open(url, "_blank", "noopener,noreferrer");
    toast.info(q
      ? t("radar.manual.extCopied", { q })
      : t("radar.manual.extOpen"));
  };

  const scanAuctions = async () => {    setAuctionScanning(true);
    try {
      await api.put("/radar/auction-config", { selected: auctSelected, keys: auctKeys });
      const r = await api.post("/radar/auctions/scan", {});
      const ps = Object.entries(r.data.per_source || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");
      toast.success(`${r.data.analyzed} vente(s) analysée(s) · ${r.data.new} nouvelle(s)${ps ? " — " + ps : ""}`);
      if (r.data.errors && r.data.errors.length) toast.error(r.data.errors.join(" · "));
      setAppliedZone(null); loadListings(null); setShowAuctionsOnly(true); setTab("results");
    } catch (e) { toast.error(e.response?.data?.detail || t("radar.auctions.scanFail")); }
    finally { setAuctionScanning(false); }
  };

  const slugify = (s) => (s || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  // Construit une URL de résultats PRÉ-FILTRÉS pour les portails au schéma d'URL fiable.
  // Renvoie null si le portail ne supporte pas les paramètres -> repli sur la page de base.
  const buildPortalDeepLink = (portal) => {
    const city = (crit.villes || "").split(",").map((s) => s.trim()).filter(Boolean)[0] || "";
    const cps = (crit.cps || "").split(",").map((s) => s.trim()).filter(Boolean);
    const pmin = crit.prix_min ? Number(crit.prix_min) : null;
    const pmax = crit.prix_max ? Number(crit.prix_max) : null;
    const smin = crit.surface_min ? Number(crit.surface_min) : null;
    const smax = crit.surface_max ? Number(crit.surface_max) : null;
    const slug = slugify(city);
    switch (portal.id) {
      case "immobiliare": {
        const base = slug ? `https://www.immobiliare.it/vendita-case/${slug}/` : "https://www.immobiliare.it/vendita-case/";
        const p = new URLSearchParams();
        if (pmin) p.set("prezzoMinimo", pmin);
        if (pmax) p.set("prezzoMassimo", pmax);
        if (smin) p.set("superficieMinima", smin);
        if (smax) p.set("superficieMassima", smax);
        const qs = p.toString();
        return qs ? `${base}?${qs}` : (slug ? base : null);
      }
      case "funda": {
        if (!slug && !(pmin || pmax)) return null;
        const parts = [];
        if (slug) parts.push(`selected_area=${encodeURIComponent(`["${slug}"]`)}`);
        if (pmin || pmax) parts.push(`price=${encodeURIComponent(`"${pmin || 0}-${pmax || ""}"`)}`);
        return `https://www.funda.nl/zoeken/koop?${parts.join("&")}`;
      }
      case "pararius": {
        if (!slug) return null;
        let u = `https://www.pararius.com/for-sale/${slug}`;
        if (pmax) u += `/${pmin || 0}-${pmax}`;
        return u;
      }
      case "immoweb": {
        const p = new URLSearchParams({ countries: "BE" });
        if (pmin) p.set("minPrice", pmin);
        if (pmax) p.set("maxPrice", pmax);
        if (cps.length) p.set("postalCodes", cps.join(","));
        const qs = p.toString();
        return qs === "countries=BE" ? null : `https://www.immoweb.be/fr/recherche/maison-et-appartement/a-vendre?${qs}`;
      }
      default:
        return null;
    }
  };

  const openPortal = (portal) => {
    if (!portal?.url) return;
    const parts = [];
    if (crit.villes) parts.push(crit.villes);
    if (crit.cps) parts.push(crit.cps);
    if (crit.property_types?.length) parts.push(crit.property_types.join(", "));
    if (crit.prix_max) parts.push(`≤ ${crit.prix_max}`);
    const q = parts.join(" · ");
    const deep = buildPortalDeepLink(portal);
    const target = deep || portal.url;
    // Presse-papiers utile surtout quand le portail n'accepte pas le pré-filtrage par URL.
    if (q && !deep && navigator.clipboard) navigator.clipboard.writeText(q).catch(() => {});
    window.open(target, "_blank", "noopener,noreferrer");
    if (deep) {
      toast.info(t("radar.manual.portalFiltered", { portal: portal.name }));
    } else {
      toast.info(q
        ? t("radar.manual.portalCopied", { portal: portal.name, q })
        : t("radar.manual.portalOpen", { portal: portal.name }));
    }
  };

  const buildCriteria = () => ({
    country: crit.country || "FR",
    villes: crit.villes ? crit.villes.split(",").map((s) => s.trim()).filter(Boolean) : [],
    cps: crit.cps ? crit.cps.split(",").map((s) => s.trim()).filter(Boolean) : [],
    state: crit.state || "", county: crit.county || "",
    property_type: null,
    property_types: crit.property_types || [],
    prix_min: crit.prix_min ? Number(crit.prix_min) : null,
    prix_max: crit.prix_max ? Number(crit.prix_max) : null,
    prix_m2_max: crit.prix_m2_max ? Number(crit.prix_m2_max) : null,
    surface_min: crit.surface_min ? Number(crit.surface_min) : null,
    surface_max: crit.surface_max ? Number(crit.surface_max) : null,
    days: crit.days ? Number(crit.days) : null,
    keywords_exclude: crit.exclude ? crit.exclude.split(",").map((s) => s.trim()).filter(Boolean) : [],
    radius_km: crit.radius_km ? Number(crit.radius_km) : 0,
    limit: 25,
  });

  const onProviderChange = async (v) => {
    setSource((s) => ({ ...s, provider: v }));
  };

  const saveKey = async () => {
    setSavingKey(true);
    try {
      const r = await api.put("/radar/source", { provider: source.provider || "realtyapi", api_key: apiKey });
      setSource(r.data); setApiKey("");
      toast.success(r.data.configured ? t("radar.source.savedConnected") : t("radar.source.savedEmpty"));
    } catch (e) { toast.error(t("radar.source.saveKeyFail")); }
    finally { setSavingKey(false); }
  };

  const labelForCriteria = (c) => {
    const parts = [];
    if (c.property_types?.length) parts.push(c.property_types.join(", "));
    else if (c.property_type) parts.push(c.property_type);
    if (c.villes?.length) parts.push(c.villes.join(", "));
    if (c.cps?.length) parts.push(c.cps.join(", "));
    if (c.radius_km) parts.push(`${c.radius_km} km`);
    if (c.surface_min) parts.push(`≥ ${Number(c.surface_min).toLocaleString("fr-FR")} m²`);
    if (c.surface_max) parts.push(`≤ ${Number(c.surface_max).toLocaleString("fr-FR")} m²`);
    if (c.prix_max) parts.push(`≤ ${Number(c.prix_max).toLocaleString("fr-FR")} €`);
    return parts.filter(Boolean).join(" · ") || (c.country || "FR");
  };

  const runSearch = async () => {
    setSearching(true);
    const c = buildCriteria();
    try {
      const r = await api.post("/radar/search", c);
      const radiusKm = c.radius_km ? Number(c.radius_km) : 0;
      if (r.data.center) setMapFocus(radiusKm ? { ...r.data.center, radius_m: radiusKm * 1000 } : r.data.center);
      const zone = { villes: c.villes || [], cps: c.cps || [], country: c.country || "FR",
                     radius_km: radiusKm, center: r.data.center || null };
      setAppliedZone(zone);
      // Résultats SPÉCIFIQUES à cette recherche (jamais un renvoi silencieux vers le global).
      // On affiche immédiatement les 5 MEILLEURES opportunités (tri par score décroissant).
      const all = (r.data.results || []).slice().sort((a, b) => (b.score || 0) - (a.score || 0));
      const count = r.data.result_count ?? all.length;
      const top5 = all.slice(0, 5);
      setSearchResults(top5);
      setSearchMeta({
        count, total: count, showing: top5.length, ranAt: new Date().toISOString(),
        criteriaLabel: labelForCriteria(c),
        status: r.data.budget_blocked ? "budget" : "done", budgetBlocked: !!r.data.budget_blocked,
        source: r.data.source, analyzed: r.data.analyzed, new: r.data.new, top: true,
        zeroMessage: t("radar.searchResult.zeroManual", "Aucun résultat trouvé pour cette recherche. Modifiez vos critères ou élargissez le périmètre."),
      });
      setHasSearched(true);
      // Rafraîchit le global EN ARRIÈRE-PLAN (onglet « Résultats » séparé) sans écraser l'écran de recherche.
      loadListings(); loadSearches();
      if (r.data.budget_blocked) {
        toast.warning(r.data.message || t("radar.watches.budgetReached", "Plafond quotidien de recherche atteint. Voici les annonces déjà collectées ; réessayez plus tard."));
      } else if (count > 0) {
        toast.success(t("radar.watches.searchOk", { n: r.data.analyzed, new: r.data.new, source: r.data.source }));
        setWatchBannerOff(false);
      } else {
        toast.info(t("radar.searchResult.zeroManual", "Aucun résultat trouvé pour cette recherche. Modifiez vos critères ou élargissez le périmètre."));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("radar.watches.searchFail"));
    } finally { setSearching(false); }
  };

  const viewSearchResults = async (s) => {
    try {
      const r = await api.get(`/radar/searches/${s.id}/results`);
      const results = r.data.results || [];
      const radiusKm = Number(s.criteria?.radius_km || 0);
      if (r.data.center) setMapFocus(radiusKm ? { ...r.data.center, radius_m: radiusKm * 1000 } : r.data.center);
      setSearchResults(results);
      setSearchMeta({
        count: r.data.result_count ?? results.length, total: r.data.result_count ?? results.length,
        showing: results.length,
        ranAt: r.data.search?.last_run || new Date().toISOString(),
        criteriaLabel: s.name, status: "saved", budgetBlocked: false, top: false,
        zeroMessage: t("radar.searchResult.zero", "Recherche terminée : aucun bien correspondant à vos critères n'a été trouvé. La surveillance reste active et vous serez alerté dès qu'une nouvelle opportunité apparaîtra."),
      });
      setAppliedZone(null);
      setHasSearched(true);
      setTab("auto");
      setTimeout(() => document.querySelector('[data-testid="radar-search-result"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } catch (e) { toast.error(e.response?.data?.detail || t("radar.watches.previewFail")); }
  };

  const saveAutoSearch = async () => {
    if (quotaReached) { toast.error(t("radar.criteria.quotaFull", { n: quotaWatches })); return; }
    const name = window.prompt(t("radar.watches.namePrompt"));
    if (!name) return;
    try {
      await api.post("/radar/searches", { name, criteria: buildCriteria(), active: true, frequency_minutes: 360, email_alerts: true });
      toast.success(t("radar.watches.savedWatch"));
      setWatchBannerOff(true);
      loadSearches(); loadEnt();
    } catch (e) { toast.error(e.response?.data?.detail || t("radar.watches.saveFail")); }
  };

  const updateSearch = async (id, patch) => {
    try {
      const r = await api.put(`/radar/searches/${id}`, patch);
      setSearches((prev) => prev.map((s) => (s.id === id ? r.data : s)));
      if ("active" in patch) loadEnt();
    } catch (e) { toast.error(e.response?.data?.detail || t("radar.watches.updateFail")); }
  };

  const runNow = async (s) => {
    setRunningId(s.id);
    try {
      const r = await api.post(`/radar/searches/${s.id}/run-now`);
      if (r.data.ok) {
        const em = r.data.email?.sent ? t("radar.watches.runEmail", { count: r.data.email.count }) : "";
        toast.success(t("radar.watches.runOk", { name: s.name, new: r.data.new, green: r.data.green, email: em }));
        loadSearches(); loadListings();
        await viewSearchResults(s);
      } else {
        toast.error(r.data.message || t("radar.watches.runNotDone"));
        loadSearches(); loadListings();
      }
    } catch (e) { toast.error(e.response?.data?.detail || t("radar.watches.runFail")); }
    finally { setRunningId(null); }
  };

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try { const r = await api.put("/radar/notif-prefs", next); setPrefs(r.data); }
    catch { toast.error(t("radar.watches.prefsFail")); }
  };

  const openPreview = async (s) => {
    try {
      const r = await api.get(`/radar/searches/${s.id}/digest-preview`);
      setPreview({ ...r.data, name: s.name });
    } catch { toast.error(t("radar.watches.previewFail")); }
  };

  const nextAlertLabel = (s) => {
    if (!s.active) return t("radar.watches.nextPause");
    const freq = s.frequency_minutes || 360;
    if (!s.last_run) return t("radar.watches.nextSoon");
    const next = new Date(new Date(s.last_run).getTime() + freq * 60000);
    if (next.getTime() <= Date.now()) return t("radar.watches.nextImminent");
    const time = next.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    const sameDay = next.toDateString() === new Date().toDateString();
    return sameDay ? t("radar.watches.nextAt", { time }) : t("radar.watches.nextOn", { date: next.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }), time });
  };

  const analyze = async () => {
    if (!form.url && !form.address && !form.city) {
      return toast.error(t("radar.analyze.needInput"));
    }
    setAnalyzing(true);
    try {
      const payload = { ...form,
        price: form.price ? Number(form.price) : null,
        surface: form.surface ? Number(form.surface) : null,
        land_surface: form.land_surface ? Number(form.land_surface) : null };
      const r = await api.post("/radar/analyze", payload);
      toast.success(t("radar.analyze.done", { score: r.data.score, band: r.data.band }));
      setSelected(r.data);
      loadListings();
      setTab("results");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("radar.analyze.fail"));
    } finally {
      setAnalyzing(false);
    }
  };

  const openDetail = async (id) => {
    try { const r = await api.get(`/radar/listings/${id}`); setSelected(r.data); }
    catch (e) {
      if (e?.response?.status === 402) {
        toast.error(t("radar.results.locked", "Opportunité verrouillée — abonnez-vous pour tout débloquer."));
        navigate("/abonnement");
      } else toast.error(t("radar.results.loadFail"));
    }
  };

  // Ouverture directe d'une fiche depuis un lien externe (ex. Dashboard « Meilleures opportunités » : /radar?listing=<id>)
  useEffect(() => {
    const lid = new URLSearchParams(location.search).get("listing");
    if (lid) { setTab("results"); openDetail(lid); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);


  const removeListing = (id) => setConfirmDelete({ mode: "single", id, count: 1 });

  const toggleSelectResult = (id) => setSelectedResults((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleSelectAll = (ids, checked) => setSelectedResults((prev) => {
    const n = new Set(prev);
    if (checked) ids.forEach((i) => n.add(i)); else ids.forEach((i) => n.delete(i));
    return n;
  });
  const performBulkDelete = async () => {
    if (!confirmDelete) return;
    setDeletingResults(true);
    try {
      if (confirmDelete.mode === "single") {
        await api.delete(`/radar/listings/${confirmDelete.id}`);
        toast.success(t("radar.results.removed"));
        setSelectedResults((prev) => { const n = new Set(prev); n.delete(confirmDelete.id); return n; });
        setSearchResults((prev) => (prev ? prev.filter((l) => l.id !== confirmDelete.id) : prev));
      } else {
        const payload = confirmDelete.mode === "all" ? { all: true } : { ids: Array.from(selectedResults) };
        const r = await api.post("/radar/listings/bulk-delete", payload);
        toast.success(t("radar.results.bulkDeleted", "{{n}} résultat(s) supprimé(s).", { n: r.data.deleted }));
        setSelectedResults(new Set());
      }
      loadListings();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("radar.results.bulkDeleteFail", "La suppression a échoué."));
    } finally { setDeletingResults(false); setConfirmDelete(null); }
  };

  // Suivi personnel d'un bien (favori / statut / note) — MàJ optimiste sans recharger toute la liste
  const trackListing = async (id, patch) => {
    setListings((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    try {
      await api.patch(`/radar/listings/${id}/track`, patch);
      if (patch.follow_status === "contacted") toast.success(t("radar.table.toastContacted"));
      else if (patch.favorite === true) toast.success(t("radar.table.toastFav"));
    } catch { toast.error(t("radar.results.loadFail")); loadListings(); }
  };

  const exportListingPdf = async (id) => {
    try {
      const r = await api.get(`/radar/listings/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `RADAR-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 403) { toast.error(t("radar.table.exportLocked")); navigate("/abonnement"); }
      else toast.error(t("radar.results.loadFail"));
    }
  };

  const deepAnalyze = async (id) => {
    try {
      const r = await api.post(`/radar/listings/${id}/deep`);
      setSelected(r.data); loadListings();
      toast.success(t("radar.watches.deepDone"));
    } catch (e) { toast.error(e.response?.data?.detail || t("radar.watches.deepFail")); }
  };

  const relaunchSearch = (s) => {
    const c = s.criteria || {};
    const nextCrit = {
      country: c.country || "FR",
      villes: (c.villes || []).join(", "), cps: (c.cps || []).join(", "),
      state: c.state || "", county: c.county || "",
      property_types: c.property_types || (c.property_type ? [c.property_type] : []),
      prix_min: c.prix_min ?? "", prix_max: c.prix_max ?? "",
      prix_m2_max: c.prix_m2_max ?? "", surface_min: c.surface_min ?? "", surface_max: c.surface_max ?? "",
      days: c.days ?? "0", exclude: (c.keywords_exclude || []).join(", "),
      radius_km: String(c.radius_km ?? 0),
    };
    setCrit(nextCrit);
    setGeoChips(chipsFromCrit(nextCrit));
    setTab("auto");
    toast.success(t("radar.watches.loadedCriteria", { name: s.name }));
  };
  const deleteSearch = async () => {
    if (!confirmDeleteSearch) return;
    setDeletingSearch(true);
    try {
      await api.delete(`/radar/searches/${confirmDeleteSearch.id}`);
      toast.success(t("radar.watches.deleted", "Recherche supprimée. La surveillance est arrêtée."));
      loadSearches(); loadEnt();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("radar.watches.deleteFail", "La suppression a échoué."));
    } finally { setDeletingSearch(false); setConfirmDeleteSearch(null); }
  };

  const now = Date.now();
  const daysUntil = (iso) => {
    if (!iso) return null;
    const d = Math.ceil((new Date(iso).getTime() - now) / 86400000);
    return d;
  };
  const filtered = listings.filter((l) => {
    if (showAuctionsOnly && l.kind !== "auction") return false;
    if (period) {
      const t = new Date(l.last_seen || l.last_analyzed || 0).getTime();
      if (now - t > period * 86400000) return false;
    }
    if (filters.type !== "all" && l.property_type !== filters.type) return false;
    if (filters.maxPrice && (l.current_price || 0) > Number(filters.maxPrice)) return false;
    if (filters.minSurface && (l.surface || 0) < Number(filters.minSurface)) return false;
    const pm2 = l.surface ? (l.current_price || 0) / l.surface : 0;
    if (filters.maxPm2 && pm2 > Number(filters.maxPm2)) return false;
    if (filters.q) { const q = filters.q.toLowerCase();
      if (!`${l.title || ""} ${l.city || ""} ${l.quartier || ""}`.toLowerCase().includes(q)) return false; }
    return true;
  });

  const mapListings = filtered.filter((l) =>
    mapFilter === "green" ? l.score >= 70 : mapFilter === "green_orange" ? l.score >= 40 : true
  );

  const resultsPanel = (
    <>
          <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[180px] flex-1">
                <Label className="text-xs text-slate-500">{t("radar.results.searchLabel")}</Label>
                <Input data-testid="radar-filter-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder={t("radar.results.searchPlaceholder")} className="mt-1 h-10" />
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-500">{t("radar.results.type")}</Label>
                <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
                  <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">{t("radar.results.all")}</SelectItem>{RADAR_TYPES.map((t2) => <SelectItem key={t2} value={t2}>{t2}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="w-32"><Label className="text-xs text-slate-500">{t("radar.results.maxPrice")}</Label><Input type="number" value={filters.maxPrice} onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })} className="mt-1 h-10" /></div>
              <div className="w-32"><Label className="text-xs text-slate-500">{t("radar.results.pm2Max")}</Label><Input type="number" value={filters.maxPm2} onChange={(e) => setFilters({ ...filters, maxPm2: e.target.value })} className="mt-1 h-10" /></div>
              <div className="w-32"><Label className="text-xs text-slate-500">{t("radar.results.minSurface")}</Label><Input type="number" value={filters.minSurface} onChange={(e) => setFilters({ ...filters, minSurface: e.target.value })} className="mt-1 h-10" /></div>
              <div className="w-36">
                <Label className="text-xs text-slate-500">{t("radar.results.displayCcy")}</Label>
                <Select value={displayCcy} onValueChange={changeCcy}>
                  <SelectTrigger className="mt-1 h-10" data-testid="radar-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["EUR", "USD", "CAD", "AUD", "AED", "TRY", "GBP", "CHF"].map((c) => (
                      <SelectItem key={c} value={c} data-testid={`radar-ccy-${c}`}>{CCY_SYMBOLS[c]} {c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {PERIODS.map((p) => (
                <button key={p.label} onClick={() => setPeriod(p.days)}
                  data-testid={`radar-period-${p.days}`}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${period === p.days ? "bg-[#0F172A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {p.label}
                </button>
              ))}
              <button onClick={() => setShowAuctionsOnly((v) => !v)} data-testid="radar-filter-auctions"
                className={`ml-2 flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold transition-colors ${showAuctionsOnly ? "bg-[#7c2d12] text-white" : "bg-amber-50 text-[#7c2d12] hover:bg-amber-100"}`}>
                <Gavel className="h-3.5 w-3.5" /> {t("radar.results.auctionsFilter")}
              </button>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="radar-empty">
              <SearchX className="mx-auto h-9 w-9 text-slate-300" />
              <p className="mt-3 text-base font-semibold text-[#0F172A]" data-testid="radar-empty-title">
                {t("radar.results.emptyTitle", "Aucune opportunité trouvée")}
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
                {t("radar.results.emptyHint", "Aucun bien ne correspond à ces critères. Élargissez la zone, augmentez le budget ou ajustez le type de bien, puis relancez la recherche.")}
              </p>
              <button onClick={() => setTab("auto")} data-testid="radar-empty-edit-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e293b]">
                <Search className="h-4 w-4" /> {t("radar.results.emptyEdit", "Modifier mes critères")}
              </button>
            </div>
          ) : (
            <>
              {appliedZone && !watchBannerOff && !quotaReached && (
                <div data-testid="radar-save-watch-banner" className="mb-4 flex flex-col items-start gap-3 rounded-2xl border border-[#C6A87C]/40 bg-gradient-to-br from-[#0F172A] to-[#1e293b] p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-[#C6A87C]" />
                    <div>
                      <p className="text-sm font-semibold text-white">{t("radar.saveWatch.title", "Enregistrez cette recherche")}</p>
                      <p className="mt-0.5 text-xs text-slate-300">{t("radar.saveWatch.body", "Soyez alerté par e-mail dès qu'une nouvelle opportunité correspond à ces critères.")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    <button data-testid="radar-save-watch-btn" onClick={saveAutoSearch}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#C6A87C] px-4 py-2 text-xs font-semibold text-[#0B1220] transition-transform hover:scale-[1.03]">
                      <BellRing className="h-3.5 w-3.5" /> {t("radar.saveWatch.cta", "Activer les alertes")}
                    </button>
                    <button data-testid="radar-save-watch-dismiss" onClick={() => setWatchBannerOff(true)} aria-label={t("radar.saveWatch.dismiss", "Fermer")}
                      className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              {/* Barre de gestion / suppression des résultats (isolée au compte courant) */}
              <div data-testid="radar-results-toolbar" className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
                <div className="flex items-center gap-2">
                  <button data-testid="radar-select-all-btn" onClick={() => toggleSelectAll(filtered.map((l) => l.id), !filtered.every((l) => selectedResults.has(l.id)))}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#0F172A] transition-colors hover:bg-slate-50">
                    <CheckSquare className="h-3.5 w-3.5" />
                    {filtered.every((l) => selectedResults.has(l.id)) ? t("radar.results.unselectAll", "Tout désélectionner") : t("radar.results.selectAll", "Tout sélectionner")}
                  </button>
                  <span className="text-xs text-slate-400" data-testid="radar-selection-count">
                    {t("radar.results.selectedCount", "{{n}} sélectionné(s) · {{total}} résultat(s)", { n: selectedResults.size, total: filtered.length })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="radar-delete-selection-btn" disabled={selectedResults.size === 0}
                    onClick={() => setConfirmDelete({ mode: "selection", count: selectedResults.size })}
                    className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40">
                    <Trash2 className="h-3.5 w-3.5" /> {t("radar.results.deleteSelection", "Supprimer la sélection")}
                  </button>
                  <button data-testid="radar-delete-all-btn" onClick={() => setConfirmDelete({ mode: "all", count: filtered.length })}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" /> {t("radar.results.deleteAll", "Vider le dossier Résultats")}
                  </button>
                </div>
              </div>
              <RadarTable
                listings={filtered}
                ccy={displayCcy}
                rates={fxRates}
                onOpen={openDetail}
                onRemove={removeListing}
                onTrack={trackListing}
                onExport={exportListingPdf}
                canExport={ent?.entitlements?.export !== false}
                navigate={navigate}
                selectedId={hoveredId}
                onHover={setHoveredId}
                selectable
                selectedIds={selectedResults}
                onToggleSelect={toggleSelectResult}
                onToggleAll={toggleSelectAll}
              />
              {filtered.some((l) => l.locked) && (
                <div data-testid="radar-trial-cta" className="mt-4 rounded-2xl border border-[#C6A87C]/40 bg-gradient-to-br from-[#0F172A] to-[#1e293b] p-5 text-center">
                  <p className="text-sm font-semibold text-white">
                    {t("radar.trial.ctaTitle", "RADAR a détecté {{n}} opportunités pour vous.", { n: filtered.length })}
                  </p>
                  <p className="mt-1 text-xs text-slate-300">
                    {t("radar.trial.ctaBody", "Pendant l'essai gratuit, vous pouvez consulter et analyser en entier la meilleure opportunité. Abonnez-vous pour débloquer toutes les autres.")}
                  </p>
                  <button data-testid="radar-trial-cta-btn" onClick={() => navigate("/abonnement")}
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#C6A87C] px-5 py-2.5 text-sm font-semibold text-[#0B1220] transition-transform hover:scale-[1.03]">
                    {t("radar.trial.ctaBtn", "Débloquer tous les résultats")}
                  </button>
                </div>
              )}
            </>
          )}
    </>
  );

  const searchResultPanel = searchMeta && (
    <div data-testid="radar-search-result">
      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="radar-search-summary">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-[#0F172A]">
              {t("radar.searchResult.doneAt", "Recherche terminée le {{date}}", { date: new Date(searchMeta.ranAt).toLocaleString("fr-FR") })}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {t("radar.searchResult.criteria", "Critères")} : {searchMeta.criteriaLabel}
            </p>
          </div>
          <span data-testid="radar-search-count"
            className={`rounded-full px-3 py-1 text-xs font-bold ${searchMeta.count > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {searchMeta.top && searchMeta.total > searchMeta.showing
              ? t("radar.searchResult.topOf", "Top {{s}} sur {{n}} résultat(s)", { s: searchMeta.showing, n: searchMeta.total })
              : t("radar.searchResult.count", "{{n}} résultat(s)", { n: searchMeta.count })}
          </span>
        </div>
      </div>

      {searchMeta.count === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="radar-search-zero">
          <SearchX className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mx-auto mt-3 max-w-xl text-sm font-semibold text-[#0F172A]" data-testid="radar-search-zero-msg">
            {searchMeta.zeroMessage || t("radar.searchResult.zeroManual", "Aucun résultat trouvé pour cette recherche. Modifiez vos critères ou élargissez le périmètre.")}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            {appliedZone && !watchBannerOff && !quotaReached && (
              <button data-testid="radar-search-zero-save" onClick={saveAutoSearch}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#C6A87C] px-4 py-2 text-sm font-semibold text-[#0B1220] transition-transform hover:scale-[1.03]">
                <BellRing className="h-4 w-4" /> {t("radar.saveWatch.cta", "Activer les alertes")}
              </button>
            )}
            <button onClick={() => setTab("auto")} data-testid="radar-search-zero-edit"
              className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e293b]">
              <Search className="h-4 w-4" /> {t("radar.results.emptyEdit", "Modifier mes critères")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {appliedZone && !watchBannerOff && !quotaReached && (
            <div data-testid="radar-save-watch-banner" className="mb-4 flex flex-col items-start gap-3 rounded-2xl border border-[#C6A87C]/40 bg-gradient-to-br from-[#0F172A] to-[#1e293b] p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-[#C6A87C]" />
                <div>
                  <p className="text-sm font-semibold text-white">{t("radar.saveWatch.title", "Enregistrez cette recherche")}</p>
                  <p className="mt-0.5 text-xs text-slate-300">{t("radar.saveWatch.body", "Soyez alerté par e-mail dès qu'une nouvelle opportunité correspond à ces critères.")}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button data-testid="radar-save-watch-btn" onClick={saveAutoSearch}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#C6A87C] px-4 py-2 text-xs font-semibold text-[#0B1220] transition-transform hover:scale-[1.03]">
                  <BellRing className="h-3.5 w-3.5" /> {t("radar.saveWatch.cta", "Activer les alertes")}
                </button>
                <button data-testid="radar-save-watch-dismiss" onClick={() => setWatchBannerOff(true)} aria-label={t("radar.saveWatch.dismiss", "Fermer")}
                  className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          <RadarTable
            listings={searchResults || []}
            ccy={displayCcy}
            rates={fxRates}
            onOpen={openDetail}
            onRemove={removeListing}
            onTrack={trackListing}
            onExport={exportListingPdf}
            canExport={ent?.entitlements?.export !== false}
            navigate={navigate}
            selectedId={hoveredId}
            onHover={setHoveredId}
          />
          {searchMeta.top && searchMeta.total > searchMeta.showing && (
            <div className="mt-3 flex items-center justify-center">
              <button data-testid="radar-search-see-all" onClick={() => setTab("results")}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-[#0F172A] transition-colors hover:bg-slate-50">
                <Search className="h-4 w-4" /> {t("radar.searchResult.seeAll", "Voir les {{n}} résultats dans « Résultats »", { n: searchMeta.total })}
              </button>
            </div>
          )}
          {(searchResults || []).some((l) => l.locked) && (
            <div data-testid="radar-trial-cta" className="mt-4 rounded-2xl border border-[#C6A87C]/40 bg-gradient-to-br from-[#0F172A] to-[#1e293b] p-5 text-center">
              <p className="text-sm font-semibold text-white">
                {t("radar.trial.ctaTitle", "RADAR a détecté {{n}} opportunités pour vous.", { n: (searchResults || []).length })}
              </p>
              <p className="mt-1 text-xs text-slate-300">
                {t("radar.trial.ctaBody", "Pendant l'essai gratuit, vous pouvez consulter et analyser en entier la meilleure opportunité. Abonnez-vous pour débloquer toutes les autres.")}
              </p>
              <button data-testid="radar-trial-cta-btn" onClick={() => navigate("/abonnement")}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#C6A87C] px-5 py-2.5 text-sm font-semibold text-[#0B1220] transition-transform hover:scale-[1.03]">
                {t("radar.trial.ctaBtn", "Débloquer tous les résultats")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="radar-page">
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="radar-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.mode === "all"
                ? t("radar.results.confirmAllTitle", "Vider le dossier Résultats ?")
                : confirmDelete?.mode === "single"
                ? t("radar.results.confirmOneTitle", "Supprimer ce résultat ?")
                : t("radar.results.confirmSelTitle", "Supprimer les résultats sélectionnés ?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.mode === "all"
                ? t("radar.results.confirmAllBody", "Cette action supprimera définitivement les {{n}} résultats de votre liste. Vos recherches enregistrées, alertes automatiques et favoris ne sont pas affectés.", { n: confirmDelete?.count || 0 })
                : confirmDelete?.mode === "single"
                ? t("radar.results.confirmOneBody", "Cette action supprimera définitivement ce résultat de votre liste. Vos recherches enregistrées, alertes et favoris ne sont pas affectés.")
                : t("radar.results.confirmSelBody", "Cette action supprimera définitivement {{n}} résultat(s). Vos recherches enregistrées, alertes et favoris ne sont pas affectés.", { n: confirmDelete?.count || 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="radar-delete-cancel">{t("common.cancel", "Annuler")}</AlertDialogCancel>
            <AlertDialogAction data-testid="radar-delete-confirm-btn" onClick={performBulkDelete} disabled={deletingResults}
              className="bg-red-600 text-white hover:bg-red-700">
              {deletingResults ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
              {t("radar.results.confirmDelete", "Supprimer définitivement")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!confirmDeleteSearch} onOpenChange={(o) => !o && setConfirmDeleteSearch(null)}>
        <AlertDialogContent data-testid="radar-search-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("radar.watches.confirmDelTitle", "Supprimer cette recherche automatique ?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("radar.watches.confirmDelBody", "« {{name}} » sera définitivement supprimée et sa surveillance arrêtée immédiatement (plus aucune consommation Apify liée à cette recherche). Vos résultats déjà détectés, vos favoris et vos autres recherches ne sont pas affectés.", { name: confirmDeleteSearch?.name || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="radar-search-delete-cancel">{t("common.cancel", "Annuler")}</AlertDialogCancel>
            <AlertDialogAction data-testid="radar-search-delete-confirm-btn" onClick={deleteSearch} disabled={deletingSearch}
              className="bg-red-600 text-white hover:bg-red-700">
              {deletingSearch ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
              {t("radar.watches.confirmDelBtn", "Supprimer la recherche")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <header className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0F172A]">
          <RadarIcon className="h-6 w-6 text-[#C6A87C]" />
        </div>
        <div>
          <p className="overline text-[#C6A87C]">{t("radar.header.overline")}</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0F172A] sm:text-4xl">RADAR</h1>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex w-full max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="auto" data-testid="radar-tab-auto">{t("radar.tabs.auto")}</TabsTrigger>
          <TabsTrigger value="analyze" data-testid="radar-tab-analyze">{t("radar.tabs.analyze")}</TabsTrigger>
          <TabsTrigger value="results" data-testid="radar-tab-results">{t("radar.tabs.results", { count: listings.length })}</TabsTrigger>
          <TabsTrigger value="map" data-testid="radar-tab-map">{t("radar.tabs.map")}</TabsTrigger>
          <TabsTrigger value="auctions" data-testid="radar-tab-auctions">{t("radar.tabs.auctions")}</TabsTrigger>
          <TabsTrigger value="searches" data-testid="radar-tab-searches">{t("radar.tabs.searches")}</TabsTrigger>
        </TabsList>

        {/* ---------- RECHERCHE AUTO ---------- */}
        <TabsContent value="auto">
          <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="radar-source-panel">
            <h3 className="mb-1 font-display text-lg text-[#0F172A]">{t("radar.source.title")}</h3>
            <p className="mb-3 text-sm text-slate-500">
              {t("radar.source.statusLabel")} {source.configured
                ? <span className="font-medium text-emerald-700" data-testid="radar-source-status">{t("radar.source.connected", { provider: source.provider })}</span>
                : <span className="font-medium text-amber-700" data-testid="radar-source-status">{t("radar.source.notConfigured")}</span>}
            </p>
            {source.meta?.[source.provider]?.requirement && (
              <div className="mb-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="radar-source-requirement">
                <span className={`mr-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${source.configured ? "bg-emerald-100 text-emerald-700" : source.meta[source.provider].access === "api_key_free" ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-800"}`}>
                  {source.configured ? t("radar.source.connectedShort") : t(`radar.source.status.${source.meta[source.provider].status}`, { defaultValue: source.meta[source.provider].status_label })}
                </span>
                {t(`radar.source.req.${source.provider}`, { defaultValue: source.meta[source.provider].requirement })}
                {source.meta[source.provider].url && <> — <a href={source.meta[source.provider].url} target="_blank" rel="noreferrer" className="text-[#C6A87C] underline">{t("radar.source.getKey")}</a></>}
              </div>
            )}
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-48">
                <Label className="text-xs text-slate-500">{t("radar.source.provider")}</Label>
                <Select value={source.provider} onValueChange={onProviderChange}>
                  <SelectTrigger className="mt-1 h-10" data-testid="radar-source-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apify">Apify — Pige Immo FR (LBC, SeLoger, PAP, Bien'ici)</SelectItem>
                    <SelectItem value="realtyapi">RealtyAPI (SeLoger, palier gratuit)</SelectItem>
                    <SelectItem value="moteurimmo">MoteurImmo (56+ portails)</SelectItem>
                    <SelectItem value="cherchertrouver">ChercherTrouver</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {source.provider === "apify" ? (
                <div className="min-w-[240px] flex-1 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="radar-apify-note">
                  {t("radar.source.apifyNoteToken")} {source.configured ? <span className="font-semibold text-emerald-700">{t("radar.source.apifyDetected")}</span> : <span className="font-semibold text-amber-700">{t("radar.source.apifyNotDetected")}</span>}
                </div>
              ) : (
                <div className="min-w-[240px] flex-1">
                  <Label className="text-xs text-slate-500">{t("radar.source.apiKey")}</Label>
                  <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={source.configured ? t("radar.source.keyPlaceholderSaved") : t("radar.source.keyPlaceholder")} data-testid="radar-source-key" className="mt-1 h-10" />
                </div>
              )}
              <Button onClick={saveKey} disabled={savingKey} data-testid="radar-source-save" className="h-10 rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                {savingKey ? <Loader2 className="h-4 w-4 animate-spin" /> : (source.provider === "apify" ? t("radar.source.useSource") : t("radar.source.saveKey"))}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
              <h3 className="font-display text-lg text-[#0F172A]">{t("radar.criteria.title")}</h3>
              <Button onClick={resetCriteria} variant="outline" data-testid="radar-new-search" className="h-9 rounded-full border-slate-300 text-[#0F172A] hover:border-[#C6A87C] hover:bg-[#C6A87C]/10">
                <RotateCw className="mr-2 h-4 w-4" /> {t("radar.criteria.newSearch")}
              </Button>
            </div>
            <p className="mb-4 text-sm text-slate-500">{t("radar.criteria.intro")}</p>
            <div className="grid gap-4">
              {countries.length > 1 && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-slate-700">{t("radar.criteria.country")}</Label>
                    <Select value={crit.country} onValueChange={(v) => { setC("country", v); setGeoChips([]); }}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="radar-crit-country"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {countries.filter((c) => c.operational).map((c) => (
                          <SelectItem key={c.code} value={c.code} data-testid={`radar-country-${c.code}`}>{c.name} ({c.currency})</SelectItem>
                        ))}
                        {countries.some((c) => !c.operational) && (
                          <div className="mt-1 border-t border-slate-100 px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t("radar.criteria.otherMarkets", "Autres marchés")}</div>
                        )}
                        {countries.filter((c) => !c.operational).map((c) => (
                          <SelectItem key={c.code} value={c.code} data-testid={`radar-country-${c.code}`}>{c.name} ({c.currency})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {crit.country !== "FR" && (
                    <div className="flex items-end" data-testid="radar-country-note">
                      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        {t("radar.criteria.intlNote")}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {(() => {
                const cc = countries.find((x) => x.code === crit.country);
                const portals = cc?.portals || [];
                if (crit.country === "FR" || cc?.auto_ready || portals.length === 0) return null;
                return (
                  <div className="rounded-xl border border-dashed border-[#C6A87C]/60 bg-[#C6A87C]/5 p-4" data-testid="radar-manual-search">
                    <div className="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-[#C6A87C]" />
                      <p className="text-sm font-semibold text-[#0F172A]">{t("radar.manual.title")}</p>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      <span dangerouslySetInnerHTML={sanitizeHtml(t("radar.manual.note", { country: cc?.name }))} />
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {portals.map((p) => {
                        const deepIds = ["immobiliare", "funda", "pararius", "immoweb"];
                        const smart = deepIds.includes(p.id);
                        return (
                        <button
                          key={p.name}
                          type="button"
                          onClick={() => openPortal(p)}
                          data-testid={`radar-portal-${crit.country}-${p.name.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#0F172A]/15 bg-white px-3 py-1.5 text-xs font-medium text-[#0F172A] transition-colors hover:border-[#C6A87C] hover:bg-[#C6A87C]/10"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> {p.name}
                          {smart && <span className="ml-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">{t("radar.manual.filtered")}</span>}
                        </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <GeoSelector country={crit.country} currency={(countries.find((x) => x.code === crit.country) || {}).currency || ""} chips={geoChips} setChips={setGeoChips} t={t} />
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-slate-700">{t("radar.criteria.radius")}</Label>
                  <Select value={String(crit.radius_km || "0")} onValueChange={(v) => setC("radius_km", v)}>
                    <SelectTrigger data-testid="radar-crit-radius" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">{t("radar.criteria.radiusCity")}</SelectItem>
                      <SelectItem value="5">5 km</SelectItem>
                      <SelectItem value="10">10 km</SelectItem>
                      <SelectItem value="20">20 km</SelectItem>
                      <SelectItem value="30">30 km</SelectItem>
                      <SelectItem value="50">50 km</SelectItem>
                      <SelectItem value="100">100 km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-4">
                <div>
                  <Label className="text-slate-700">{t("radar.criteria.types")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="radar-crit-type" className="mt-1.5 flex h-11 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 text-left text-sm text-slate-700">
                        <span className="truncate">{crit.property_types.length ? crit.property_types.join(", ") : t("radar.criteria.allTypes")}</span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-56 p-2" data-testid="radar-type-menu">
                      <div className="max-h-64 overflow-auto">
                        {RADAR_TYPES.map((t) => (
                          <button key={t} type="button" onClick={() => toggleType(t)} data-testid={`radar-type-opt-${t}`}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-100">
                            <span className={`flex h-4 w-4 items-center justify-center rounded border ${crit.property_types.includes(t) ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-300"}`}>
                              {crit.property_types.includes(t) && <Check className="h-3 w-3" />}
                            </span>
                            <span className="text-slate-700">{t}</span>
                          </button>
                        ))}
                      </div>
                      {crit.property_types.length > 0 && (
                        <button type="button" onClick={() => setC("property_types", [])} data-testid="radar-type-clear" className="mt-1 w-full rounded-md px-2 py-1.5 text-left text-xs text-slate-400 hover:bg-slate-50">{t("radar.criteria.clearSelection")}</button>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
                <div><Label className="text-slate-700">{t("radar.criteria.priceMin")}</Label><Input data-testid="radar-crit-pmin" type="number" value={crit.prix_min} onChange={(e) => setC("prix_min", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label className="text-slate-700">{t("radar.criteria.priceMax")}</Label><Input data-testid="radar-crit-pmax" type="number" value={crit.prix_max} onChange={(e) => setC("prix_max", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label className="text-slate-700">{t("radar.criteria.pm2Max")}</Label><Input data-testid="radar-crit-pm2" type="number" value={crit.prix_m2_max} onChange={(e) => setC("prix_m2_max", e.target.value)} className="mt-1.5 h-11" /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-4">
                <div><Label className="text-slate-700">{t("radar.criteria.surfaceMin")}</Label><Input data-testid="radar-crit-smin" type="number" value={crit.surface_min} onChange={(e) => setC("surface_min", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label className="text-slate-700">{t("radar.criteria.surfaceMax")}</Label><Input data-testid="radar-crit-smax" type="number" value={crit.surface_max} onChange={(e) => setC("surface_max", e.target.value)} className="mt-1.5 h-11" /></div>
                <div>
                  <Label className="text-slate-700">{t("radar.criteria.period")}</Label>
                  <Select value={String(crit.days)} onValueChange={(v) => setC("days", v)}>
                    <SelectTrigger data-testid="radar-crit-days" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">{t("radar.criteria.daysAll", "Toutes les dates")}</SelectItem><SelectItem value="1">{t("radar.criteria.days1")}</SelectItem><SelectItem value="3">{t("radar.criteria.days3")}</SelectItem>
                      <SelectItem value="7">{t("radar.criteria.days7")}</SelectItem><SelectItem value="14">{t("radar.criteria.days14")}</SelectItem>
                      <SelectItem value="30">{t("radar.criteria.days30")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-slate-700">{t("radar.criteria.exclude")}</Label><Input data-testid="radar-crit-exclude" value={crit.exclude} onChange={(e) => setC("exclude", e.target.value)} className="mt-1.5 h-11" /></div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={runSearch} disabled={searching || !source.configured} data-testid="radar-search-btn" className="rounded-full bg-[#C6A87C] px-6 text-[#0F172A] hover:bg-[#b8986a]">
                  {searching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} {t("radar.criteria.run")}
                </Button>
                <Button onClick={saveAutoSearch} variant="outline" disabled={quotaReached} data-testid="radar-save-auto-search" className="rounded-full">
                  <BellRing className="mr-2 h-4 w-4" /> {t("radar.criteria.saveWatch")}
                </Button>
                {ent && (
                  <button data-testid="radar-quota-badge" type="button" onClick={() => setTab("searches")}
                    title={t("radar.criteria.openWatches", "Ouvrir mes recherches enregistrées")}
                    className={`cursor-pointer rounded-full px-3 py-1 text-xs font-semibold underline-offset-2 transition-colors hover:underline ${quotaReached ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {quotaWatches === null ? `${t("radar.criteria.watchesActive", { n: activeWatches })} · ${t("radar.criteria.unlimited")}` : `${activeWatches}/${quotaWatches} ${t("radar.criteria.watchesActive", { n: "" }).trim()}`}
                  </button>
                )}
                {quotaReached && (
                  <span className="text-xs text-amber-700" data-testid="radar-quota-upgrade">
                    {t("radar.criteria.limitReached")} <a href="/abonnement" className="font-semibold underline">{t("radar.criteria.upgrade")}</a>
                  </span>
                )}
                {!source.configured && <span className="text-xs text-amber-700">{t("radar.criteria.configureFirst")}</span>}
              </div>

              {/* Sources premium supplémentaires — cochables, cache 24 h, gating par abonnement */}
              {extraSources.length > 0 && (
                <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="radar-extra-sources">
                  <div className="mb-1 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#C6A87C]" />
                    <h4 className="font-display text-sm font-semibold text-[#0F172A]">{t("radar.premium.title")}</h4>
                  </div>
                  <p className="mb-3 text-xs text-slate-500">
                    {t("radar.premium.subtitle")}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {extraSources.map((s) => (
                      <label key={s.id} data-testid={`radar-extra-${s.id}`}
                        className={`flex flex-col gap-1 rounded-lg border p-3 text-left transition ${s.locked ? "cursor-not-allowed border-slate-200 bg-slate-100 opacity-70" : extraSelected.includes(s.id) ? "cursor-pointer border-[#C6A87C] bg-[#C6A87C]/10" : "cursor-pointer border-slate-200 bg-white hover:border-[#C6A87C]/60"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
                            {s.kind === "auction" && <Gavel className="h-3.5 w-3.5 text-slate-500" />}{t(`radar.premium.sources.${s.id}.name`, { defaultValue: s.name })}
                          </span>
                          {s.locked ? <Lock className="h-4 w-4 text-slate-400" data-testid={`radar-extra-lock-${s.id}`} />
                            : <input type="checkbox" data-testid={`radar-extra-check-${s.id}`} checked={extraSelected.includes(s.id)}
                                disabled={s.locked} onChange={() => toggleExtra(s.id, s.locked)} className="h-4 w-4 accent-[#C6A87C]" />}
                        </div>
                        <span className="text-[11px] leading-snug text-slate-500">{t(`radar.premium.sources.${s.id}.desc`, { defaultValue: s.desc })}</span>
                        {s.locked ? (
                          <span className="mt-0.5 inline-flex w-fit items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`radar-extra-lockmsg-${s.id}`}>
                            <Lock className="h-3 w-3" /> {t("radar.premium.lockReason", { defaultValue: s.lock_reason })}
                          </span>
                        ) : (
                          <span className="mt-0.5 text-[10px] text-slate-400">{t("radar.premium.costLabel")} {t(`radar.premium.sources.${s.id}.cost`, { defaultValue: s.cost })}</span>
                        )}
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Button onClick={runExtraSources} disabled={extraRunning || !source.configured || extraSelected.length === 0}
                      data-testid="radar-extra-run" className="rounded-full bg-[#0F172A] px-5 text-white hover:bg-[#1e293b]">
                      {extraRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                      {t("radar.premium.run")}
                    </Button>
                    {extraSources.some((s) => s.locked) && (
                      <span className="text-xs text-slate-500" dangerouslySetInnerHTML={sanitizeHtml(t("radar.premium.auctionNote"))} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        {hasSearched && (
          <div className="mt-8 border-t border-slate-200 pt-6" data-testid="radar-inline-results">
            <h3 className="mb-4 font-display text-xl tracking-tight text-[#0F172A]" data-testid="radar-inline-results-title">{t("radar.results.inlineTitle", "Résultats de la recherche")}</h3>
            {searchResultPanel}
          </div>
        )}
        </TabsContent>

        {/* ---------- ANALYSE ---------- */}
        <TabsContent value="analyze">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <p className="mb-4 text-sm text-slate-500">
              <span dangerouslySetInnerHTML={sanitizeHtml(t("radar.analyze.intro"))} />
            </p>
            <div className="grid gap-4">
              <div>
                <Label className="flex items-center gap-2 text-slate-700"><Link2 className="h-4 w-4 text-[#C6A87C]" /> {t("radar.analyze.link")}</Label>
                <Input data-testid="radar-url" value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://..." className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="flex items-center gap-2 text-slate-700"><MapPin className="h-4 w-4 text-[#C6A87C]" /> {t("radar.analyze.address")}</Label>
                <Input data-testid="radar-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div><Label className="text-slate-700">{t("radar.analyze.city")}</Label><Input data-testid="radar-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label className="text-slate-700">{t("radar.analyze.arr")}</Label><Input data-testid="radar-arr" value={form.arrondissement} onChange={(e) => set("arrondissement", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label className="text-slate-700">{t("radar.analyze.quartier")}</Label><Input data-testid="radar-quartier" value={form.quartier} onChange={(e) => set("quartier", e.target.value)} className="mt-1.5 h-11" /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-4">
                <div>
                  <Label className="text-slate-700">{t("radar.analyze.type")}</Label>
                  <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                    <SelectTrigger data-testid="radar-type" className="mt-1.5 h-11"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>{RADAR_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-slate-700">{t("radar.analyze.price")}</Label><Input data-testid="radar-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label className="text-slate-700">{t("radar.analyze.surface")}</Label><Input data-testid="radar-surface" type="number" value={form.surface} onChange={(e) => set("surface", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label className="text-slate-700">{t("radar.analyze.land")}</Label><Input data-testid="radar-land" type="number" value={form.land_surface} onChange={(e) => set("land_surface", e.target.value)} className="mt-1.5 h-11" /></div>
              </div>
              <div>
                <Label className="text-slate-700">{t("radar.analyze.descLabel")}</Label>
                <Textarea data-testid="radar-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder={t("radar.analyze.descPlaceholder")} className="mt-1.5" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={analyze} disabled={analyzing} data-testid="radar-analyze-btn" className="rounded-full bg-[#0F172A] px-6 text-white hover:bg-[#1e293b]">
                  {analyzing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} {t("radar.analyze.analyzeBtn")}
                </Button>
                <Button variant="outline" onClick={() => setForm(EMPTY)} className="rounded-full">{t("radar.analyze.reset")}</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ---------- RESULTATS ---------- */}
        <TabsContent value="results">
          {resultsPanel}
        </TabsContent>

        {/* ---------- CARTE ---------- */}
        <TabsContent value="map">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-500">{t("radar.map.show")}</span>
            {[
              { k: "green", label: t("radar.map.green"), on: "text-emerald-300", off: "text-emerald-700", weight: "font-bold" },
              { k: "green_orange", label: t("radar.map.greenOrange"), on: "text-amber-300", off: "text-amber-600", weight: "font-bold" },
              { k: "all", label: t("radar.map.all"), on: "text-white", off: "text-slate-600", weight: "font-medium" },
            ].map((o) => (
              <button key={o.k} onClick={() => { setMapFilter(o.k); setShowAuctionsOnly(false); }} data-testid={`radar-mapfilter-${o.k}`}
                className={`rounded-full px-3 py-1 text-xs ${o.weight} transition-colors ${mapFilter === o.k && !showAuctionsOnly ? `bg-[#0F172A] ${o.on}` : `bg-slate-100 ${o.off} hover:bg-slate-200`}`}>
                {o.label}
              </button>
            ))}
            <button onClick={() => setShowAuctionsOnly((v) => !v)} data-testid="radar-mapfilter-auctions"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold transition-colors ${showAuctionsOnly ? "bg-[#7c2d12] text-white" : "bg-amber-50 text-[#7c2d12] hover:bg-amber-100"}`}>
              <Gavel className="h-3.5 w-3.5" /> {t("radar.map.auctions")}
            </button>
            <span className="ml-auto text-xs text-slate-400">{t("radar.map.approxLegend")}</span>
          </div>
          {mapListings.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400" data-testid="radar-map-empty">
              {t("radar.map.empty")}
            </div>
          ) : (
            <RadarMap listings={mapListings} onOpen={openDetail} country={crit.country} focus={mapFocus} selectedId={hoveredId} onHover={setHoveredId} />
          )}
        </TabsContent>

        {/* ---------- VENTES AUX ENCHÈRES ---------- */}
        <TabsContent value="auctions">
          {ent && !canAuctions && (
            <div className="mb-4 flex items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="radar-auctions-locked">
              <div>
                <h3 className="flex items-center gap-2 font-display text-lg text-[#0F172A]"><Gavel className="h-5 w-5 text-amber-600" /> {t("radar.auctions.premiumTitle")}</h3>
                <p className="mt-1 text-sm text-amber-800"><span dangerouslySetInnerHTML={sanitizeHtml(t("radar.auctions.premiumNote"))} /></p>
              </div>
              <a href="/abonnement" data-testid="radar-auctions-upgrade-btn" className="shrink-0 rounded-full bg-[#0F172A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e293b]">{t("radar.auctions.goPremium")}</a>
            </div>
          )}
          <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="radar-auction-panel">
            <h3 className="mb-1 flex items-center gap-2 font-display text-lg text-[#0F172A]"><Gavel className="h-5 w-5 text-[#C6A87C]" /> {t("radar.auctions.panelTitle")}</h3>
            <p className="mb-4 text-sm text-slate-500">
              <span dangerouslySetInnerHTML={sanitizeHtml(t("radar.auctions.panelNote"))} />
            </p>

            <div className="rounded-xl border border-slate-100 p-4">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium text-[#0F172A]">{t("radar.auctions.sourcesStatus")}</span>
                <button onClick={selectAllAuctProv} data-testid="radar-auction-selectall" className="text-xs font-medium text-[#C6A87C] hover:underline">
                  {auctSelected.length === selectableKeys.length && selectableKeys.length > 0 ? t("radar.auctions.deselectAll") : t("radar.auctions.selectAll")}
                </button>
              </div>
              <p className="mb-3 text-xs text-slate-400"><span dangerouslySetInnerHTML={sanitizeHtml(t("radar.auctions.caseByCase"))} /></p>
              <div className="space-y-3">
                {auctProviderKeys.map((p) => {
                  const m = auctMeta[p] || {};
                  const status = m.status || "unavailable";
                  const badgeCls = {
                    connected: "bg-emerald-100 text-emerald-700",
                    third_party: "bg-indigo-100 text-indigo-700",
                    api_key: "bg-amber-100 text-amber-800",
                    external_account: "bg-amber-100 text-amber-800",
                    unavailable: "bg-slate-200 text-slate-500",
                  }[status] || "bg-slate-200 text-slate-500";
                  const selectable = m.selectable ?? true;
                  return (
                  <div key={p} className={`rounded-lg border p-3 ${selectable ? "border-slate-100" : "border-slate-100 bg-slate-50"}`} data-testid={`radar-auction-src-${p}`}>
                    <div className="flex items-start justify-between gap-2">
                      <button onClick={() => toggleAuctProv(p)} disabled={!selectable} data-testid={`radar-auction-check-${p}`} className={`flex items-center gap-2 text-left ${selectable ? "" : "cursor-not-allowed"}`}>
                        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${auctSelected.includes(p) ? "border-[#0F172A] bg-[#0F172A] text-white" : selectable ? "border-slate-300" : "border-slate-200 bg-slate-100"}`}>
                          {auctSelected.includes(p) && <Check className="h-3 w-3" />}
                        </span>
                        <span className={`text-sm font-medium ${selectable ? "text-slate-700" : "text-slate-400"}`}>{auctProviders[p]}</span>
                      </button>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badgeCls}`} data-testid={`radar-auction-status-${p}`}>{m.status_label || t("radar.auctions.unknownStatus")}</span>
                    </div>
                    {m.requirement && <p className="ml-6 mt-1.5 text-[11px] leading-relaxed text-slate-500" data-testid={`radar-auction-req-${p}`}>{m.requirement}{m.url && <> — <a href={m.url} target="_blank" rel="noreferrer" className="text-[#C6A87C] underline">{t("radar.auctions.officialSite")}</a></>}</p>}
                    {status !== "connected" && m.url && (
                      <button onClick={() => openSourceSite(p)} data-testid={`radar-auction-open-${p}`}
                        className="ml-6 mt-2 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-[#C6A87C] hover:text-[#0F172A]">
                        <ExternalLink className="h-3 w-3" /> {t("radar.auctions.openSource")}
                      </button>
                    )}
                    {selectable && auctSelected.includes(p) && m.needs_key && (
                      <Input type="password" value={auctKeys[p] || ""} onChange={(e) => setAuctKeys((k) => ({ ...k, [p]: e.target.value }))}
                        placeholder={auctConfigured[p] ? t("radar.auctions.keySaved") : t("radar.auctions.keyThirdParty")}
                        data-testid={`radar-auction-key-${p}`} className="ml-6 mt-2 h-9 text-sm" style={{ width: "calc(100% - 1.5rem)" }} />
                    )}
                  </div>
                  );
                })}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button onClick={saveAuctionConfig} variant="outline" data-testid="radar-auction-save" className="h-10 rounded-full">{t("radar.auctions.saveSources")}</Button>
                <Button onClick={() => scanAuctions()} disabled={auctionScanning || auctSelected.length === 0 || !canAuctions} data-testid="radar-auction-scan" className="h-10 rounded-full bg-[#C6A87C] px-6 text-[#0F172A] hover:bg-[#b8986a]">
                  {auctionScanning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gavel className="mr-2 h-4 w-4" />} {t("radar.auctions.scan")}
                </Button>
                {auctSelected.length === 0 && <span className="text-xs text-amber-700">{t("radar.auctions.checkOne")}</span>}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            <span dangerouslySetInnerHTML={sanitizeHtml(t("radar.auctions.footer"))} />
          </div>
        </TabsContent>

        {/* ---------- RECHERCHES ENREGISTRÉES (surveillance auto) ---------- */}
        <TabsContent value="searches">
          {/* Préférences de notification */}
          {prefs && (
            <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-6" data-testid="radar-notif-prefs">
              <h3 className="mb-1 flex items-center gap-2 font-display text-lg text-[#0F172A]"><BellRing className="h-5 w-5 text-[#C6A87C]" /> {t("radar.watches.prefsTitle")}</h3>
              <p className="mb-4 text-sm text-slate-500"><span dangerouslySetInnerHTML={sanitizeHtml(t("radar.watches.prefsIntro"))} /></p>
              <div className="mb-4 rounded-xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-500">
                <span dangerouslySetInnerHTML={sanitizeHtml(t("radar.watches.selectionRule"))} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-center justify-between rounded-xl border border-slate-100 p-4">
                  <div><p className="text-sm font-medium text-[#0F172A]">{t("radar.watches.inApp")}</p><p className="text-xs text-slate-400">{t("radar.watches.inAppSub")}</p></div>
                  <Switch checked={prefs.in_app_enabled} onCheckedChange={(v) => savePrefs({ in_app_enabled: v })} data-testid="pref-inapp-toggle" />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-slate-100 p-4">
                  <div><p className="text-sm font-medium text-[#0F172A]">{t("radar.watches.dailyEmail")}</p><p className="text-xs text-slate-400">{t("radar.watches.dailyEmailSub")}</p></div>
                  <Switch checked={prefs.email_enabled} onCheckedChange={(v) => savePrefs({ email_enabled: v })} data-testid="pref-email-toggle" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{t("radar.watches.minScore")}</Label>
                  <Select value={String(prefs.notif_min_score)} onValueChange={(v) => savePrefs({ notif_min_score: Number(v) })}>
                    <SelectTrigger className="mt-1 h-10" data-testid="pref-notif-score"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">{t("radar.watches.scoreAll")}</SelectItem>
                      <SelectItem value="40">{t("radar.watches.scoreOrange")}</SelectItem>
                      <SelectItem value="70">{t("radar.watches.scoreGreen")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{t("radar.watches.recvEmail")}</Label>
                  <div className="mt-1 flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600" data-testid="pref-email-to-note">
                    {t("radar.watches.recvEmailNote")} <a href="/mon-compte" className="ml-1 font-medium text-[#C6A87C] underline">{t("radar.watches.manageAccount")}</a>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 font-display text-lg text-[#0F172A]"><Clock className="h-5 w-5 text-[#C6A87C]" /> {t("radar.watches.autoTitle")}</h3>
              {ent && (
                <span data-testid="radar-searches-quota" className={`rounded-full px-3 py-1 text-xs font-semibold ${quotaReached ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                  {quotaWatches === null ? `${t("radar.watches.activeCount", { n: activeWatches })} · ${t("radar.criteria.unlimited")}` : `${activeWatches}/${quotaWatches} ${t("radar.watches.activeCount", { n: "" }).trim()}`}
                </span>
              )}
            </div>
            {searches.length === 0 ? (
              <p className="text-slate-400" data-testid="radar-searches-empty">{t("radar.watches.empty")}</p>
            ) : (
              <div className="space-y-3">
                {searches.map((s) => (
                  <div key={s.id} className="rounded-xl border border-slate-100 p-4" data-testid={`radar-search-${s.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[#0F172A]">{s.name}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.active ? t("radar.watches.active") : t("radar.watches.paused")}</span>
                          {typeof s.last_result_count === "number" && (
                            <span data-testid={`radar-search-rescount-${s.id}`} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                              {t("radar.searchResult.count", "{{n}} résultat(s)", { n: s.last_result_count })}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-slate-400" data-testid={`radar-search-crit-${s.id}`}>
                          {[(s.criteria?.villes || []).join(", "),
                            (s.criteria?.cps || []).join(", "),
                            (s.criteria?.property_types || []).join(", ") || s.criteria?.property_type,
                            s.criteria?.radius_km ? `${s.criteria.radius_km} km` : null,
                            s.criteria?.surface_min ? `≥ ${Number(s.criteria.surface_min).toLocaleString("fr-FR")} m²` : null,
                            s.criteria?.prix_max ? `≤ ${Number(s.criteria.prix_max).toLocaleString("fr-FR")} €` : null].filter(Boolean).join(" · ") || "—"}
                        </p>
                        {s.created_at && (
                          <p className="mt-1 text-[11px] text-slate-400" data-testid={`radar-search-created-${s.id}`}>
                            {t("radar.watches.createdOn", "Créée le")} {new Date(s.created_at).toLocaleString("fr-FR")}
                          </p>
                        )}
                        <p className="mt-1 text-[11px] text-slate-400" data-testid={`radar-search-lastrun-${s.id}`}>
                          {s.last_run ? t("radar.watches.lastRun", { date: new Date(s.last_run).toLocaleString("fr-FR"), status: s.last_run_status || "" }) : t("radar.watches.never")}
                          {typeof s.total_new === "number" ? ` · ${t("radar.watches.totalDetected", { n: s.total_new })}` : ""}
                        </p>
                        <p className="mt-1 flex items-center gap-1.5 text-[11px] font-medium" data-testid={`radar-search-next-${s.id}`}>
                          <span className={`inline-block h-1.5 w-1.5 rounded-full ${s.active ? "animate-pulse bg-emerald-500" : "bg-slate-300"}`} />
                          <span className={s.active ? "text-emerald-700" : "text-slate-400"}>
                            {s.active ? t("radar.watches.nextAlert", { when: nextAlertLabel(s) }) : t("radar.watches.watchPaused")}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => viewSearchResults(s)} data-testid={`radar-search-view-${s.id}`} className="rounded-full border-[#C6A87C] text-[#8a6d3b] hover:bg-[#C6A87C]/10">
                          <Search className="mr-1.5 h-3.5 w-3.5" /> {t("radar.watches.viewResults", "Voir les résultats")}
                        </Button>
                        <Button size="sm" onClick={() => runNow(s)} disabled={runningId === s.id} data-testid={`radar-search-run-${s.id}`} className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                          {runningId === s.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />} {t("radar.watches.run")}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => relaunchSearch(s)} className="rounded-full"><RotateCw className="mr-1.5 h-3.5 w-3.5" /> {t("radar.watches.edit")}</Button>
                        <Button size="sm" variant="outline" onClick={() => openPreview(s)} data-testid={`radar-search-preview-${s.id}`} className="rounded-full"><Mail className="mr-1.5 h-3.5 w-3.5" /> {t("radar.watches.emailPreview")}</Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteSearch(s)} data-testid={`radar-search-del-${s.id}`} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-slate-50 pt-3">
                      <div className="flex items-center gap-2">
                        <Switch checked={s.active} onCheckedChange={(v) => updateSearch(s.id, { active: v })} data-testid={`radar-search-active-${s.id}`} />
                        <span className="text-xs text-slate-500">{t("radar.watches.watchActive")}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 text-slate-400" />
                        <Select value={String(s.frequency_minutes || 360)} onValueChange={(v) => updateSearch(s.id, { frequency_minutes: Number(v) })}>
                          <SelectTrigger className="h-8 w-44 text-xs" data-testid={`radar-search-freq-${s.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f.v} value={String(f.v)}>{t(`radar.watches.freq${f.v}`)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-slate-400" />
                        <Switch checked={s.email_alerts} onCheckedChange={(v) => updateSearch(s.id, { email_alerts: v })} data-testid={`radar-search-email-${s.id}`} />
                        <span className="text-xs text-slate-500">E-mail récapitulatif (1/jour)</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <RadarDetail key={selected?.id} listing={selected} onClose={() => setSelected(null)} onChanged={loadListings} onDeep={deepAnalyze} pluEntitled={ent?.entitlements?.plu !== false} />

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="digest-preview-overlay">
          <div className="absolute inset-0 bg-black/70" onClick={() => setPreview(null)} data-testid="digest-preview-backdrop" />
          <div className="relative z-10 flex max-h-[85vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <div>
                <p className="font-display text-lg text-[#0F172A]">{t("radar.preview.title")}</p>
                <p className="text-xs text-slate-500">
                  {t("radar.preview.sub", { name: preview.name, count: preview.count, green: preview.green, orange: preview.orange })}
                </p>
              </div>
              <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-700" data-testid="digest-preview-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 p-3">
              {preview.count === 0 ? (
                <div className="p-10 text-center text-sm text-slate-400">{t("radar.preview.empty")}</div>
              ) : (
                <iframe title="preview" srcDoc={preview.html} className="h-[60vh] w-full rounded-lg border-0 bg-white" data-testid="digest-preview-iframe" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
